/**
 * SegmentedToggle — a small horizontal "pick exactly one" control (segmented
 * button group). The brand's single-select toggle atom, used by the Research
 * Intake (Purpose / Grade-scope / Verify-strictness — E06-F01-T06/T07) and the
 * Internal-Notification template switch (Standard / Confidential — E03-F07-T05).
 *
 * Parity with the legacy prototype's pill-toggle: a row of buttons where the
 * active option is highlighted with the navy/gold brand tokens; clicking an
 * option fires `onChange(value)`. Keyboard: each button is focusable; Arrow
 * Left/Right move the selection (roving via the native button tab order), Enter/
 * Space activate (native button behaviour). Renders as `role="group"`.
 *
 * Presentational only — controlled via `value`/`onChange`. All colours come from
 * the design tokens (Tailwind classes referencing the navy/gold CSS vars); no
 * raw hex.
 */
import type { JSX } from 'react';

export interface SegmentedOption<T extends string> {
  /** The machine value persisted into criteria / template state. */
  readonly value: T;
  /** The human label rendered on the button. */
  readonly label: string;
}

export interface SegmentedToggleProps<T extends string> {
  /** The available options, rendered left-to-right. */
  readonly options: readonly SegmentedOption<T>[];
  /** The currently-selected value (controlled). */
  readonly value: T;
  /** Fired with the picked value when an option is clicked. */
  readonly onChange: (value: T) => void;
  /** Accessible label for the group (e.g. "Research purpose"). */
  readonly ariaLabel: string;
  /** Optional test id on the root group. */
  readonly testId?: string;
  /** Disable the whole control. */
  readonly disabled?: boolean;
}

/** Active-segment classes — navy fill, surface text (brand primary tokens). */
const ACTIVE_CLASS = 'bg-[var(--color-primary)] text-white shadow-sm';
/** Idle-segment classes — surface with muted text, navy on hover. */
const IDLE_CLASS =
  'bg-white text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] hover:bg-[var(--color-surface-muted)]';

/**
 * One-of-N segmented toggle. Each segment is a `<button aria-pressed>` so screen
 * readers announce the selected state; the group carries the supplied label.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  testId = 'segmented-toggle',
  disabled = false,
}: SegmentedToggleProps<T>): JSX.Element {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className="inline-flex rounded-md border border-[var(--color-border)] p-0.5 gap-0.5 bg-white"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            data-testid={`${testId}-${opt.value}`}
            data-active={active ? 'true' : 'false'}
            className={`min-h-[36px] px-3 py-1.5 text-sm font-medium rounded transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] disabled:opacity-50 ${
              active ? ACTIVE_CLASS : IDLE_CLASS
            }`}
            onClick={() => {
              if (!disabled && opt.value !== value) onChange(opt.value);
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
